import { Globe, Languages, Plus, Trash2 } from "lucide-react";
import * as React from "react";

import { type TranslationOut, translationsApi } from "@/api/translations";
import { EmptyData } from "@/components/illustrations/EmptyData";
import { AddLanguageDialog } from "@/i18n/AddLanguageDialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { EmptyState } from "@/components/ui/empty-state";
import { Input } from "@/components/ui/input";
import { LoadingTable } from "@/components/ui/loading-table";
import { useUIStore } from "@/stores/ui";

/**
 * TranslationsPage — quản lý label đa ngôn ngữ.
 *
 * Layout: pivot table — keys × locales. Mỗi cell là input edit-in-place.
 * Save 1 cell → upsert. Add language → tạo column mới.
 */
export function TranslationsPage(): JSX.Element {
  const pushToast = useUIStore((s) => s.pushToast);

  const [items, setItems] = React.useState<TranslationOut[]>([]);
  const [locales, setLocales] = React.useState<string[]>([]);
  const [namespace, setNamespace] = React.useState<string>("ui");
  const [namespaces, setNamespaces] = React.useState<string[]>([]);
  const [keyFilter, setKeyFilter] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [addLanguageOpen, setAddLanguageOpen] = React.useState(false);
  const [draft, setDraft] = React.useState<{ key: string; locale: string; value: string } | null>(null);
  const [newKey, setNewKey] = React.useState("");

  const refresh = React.useCallback(async () => {
    setBusy(true);
    try {
      const [list, ls, ns] = await Promise.all([
        translationsApi.list({ namespace, key_prefix: keyFilter || undefined }),
        translationsApi.listLocales(),
        translationsApi.listNamespaces(),
      ]);
      setItems(list);
      setLocales(ls.length > 0 ? ls : ["vi", "en"]);
      setNamespaces(ns.length > 0 ? ns : ["ui"]);
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Lỗi",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setBusy(false);
    }
  }, [namespace, keyFilter, pushToast]);

  React.useEffect(() => {
    void refresh();
  }, [refresh]);

  // Pivot: keys[] × locale → value
  const byKey: Record<string, Record<string, TranslationOut>> = {};
  for (const t of items) {
    (byKey[t.key] ??= {})[t.locale] = t;
  }
  const keys = Object.keys(byKey).sort();

  const handleSaveCell = async (
    key: string,
    locale: string,
    value: string,
  ): Promise<void> => {
    setBusy(true);
    try {
      await translationsApi.upsert({
        locale,
        namespace,
        key,
        value,
      });
      await refresh();
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Lưu thất bại",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setBusy(false);
      setDraft(null);
    }
  };

  const handleDelete = async (id: string): Promise<void> => {
    if (!confirm("Xoá entry này?")) return;
    setBusy(true);
    try {
      await translationsApi.remove(id);
      await refresh();
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Xoá thất bại",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setBusy(false);
    }
  };

  const handleAddKey = async (): Promise<void> => {
    if (!newKey || !newKey.match(/^[a-zA-Z0-9_.\-]+$/)) {
      pushToast({
        variant: "warning",
        title: "Key chỉ chữ/số/_./- — vd: 'button.save'",
      });
      return;
    }
    setBusy(true);
    try {
      // Bootstrap entry trong locale đầu tiên
      const firstLocale = locales[0] ?? "vi";
      await translationsApi.upsert({
        locale: firstLocale,
        namespace,
        key: newKey,
        value: newKey, // placeholder — admin sửa
      });
      setNewKey("");
      await refresh();
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Thêm key thất bại",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex-1 flex flex-col">
      <header className="h-12 shrink-0 flex items-center px-6 gap-3 border-b bg-background/50">
        <h1 className="font-semibold flex items-center gap-2">
          <Languages className="size-4" /> Translations
        </h1>
        <Button
          size="sm"
          variant="outline"
          className="ml-auto"
          onClick={() => setAddLanguageOpen(true)}
        >
          <Globe className="size-4 mr-1" /> Thêm ngôn ngữ
        </Button>
      </header>

      <main className="flex-1 max-w-7xl w-full mx-auto p-6 space-y-4 overflow-y-auto">
        {/* Filters */}
        <Card>
          <CardContent className="p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-medium block mb-1">Namespace</label>
              <div className="flex gap-1">
                <select
                  value={namespace}
                  onChange={(e) => setNamespace(e.target.value)}
                  className="flex-1 h-8 rounded-md border bg-background px-2 text-sm"
                >
                  {[...new Set([...namespaces, "ui", "form", "notification"])].sort().map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
                <Input
                  value={namespace}
                  onChange={(e) => setNamespace(e.target.value)}
                  placeholder="custom"
                  className="h-8 text-xs flex-1"
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium block mb-1">Key prefix filter</label>
              <Input
                value={keyFilter}
                onChange={(e) => setKeyFilter(e.target.value)}
                placeholder="button."
                className="h-8 font-mono"
              />
            </div>
            <div>
              <label className="text-xs font-medium block mb-1">Locales available</label>
              <div className="flex flex-wrap gap-1 pt-1">
                {locales.map((l) => (
                  <Badge key={l} variant="outline" className="text-[10px]">
                    {l}
                  </Badge>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Add new key */}
        <Card>
          <CardContent className="p-4 flex items-center gap-2">
            <Input
              value={newKey}
              onChange={(e) => setNewKey(e.target.value)}
              placeholder={`New key trong namespace "${namespace}", vd: button.save`}
              className="font-mono flex-1"
            />
            <Button onClick={() => void handleAddKey()} disabled={busy || !newKey}>
              <Plus className="size-4 mr-1" /> Thêm key
            </Button>
          </CardContent>
        </Card>

        {/* Pivot table */}
        {busy && keys.length === 0 ? (
          <LoadingTable rows={6} cols={3} />
        ) : keys.length === 0 ? (
          <Card>
            <CardContent className="p-6">
              <EmptyState
                hero={<EmptyData size={120} />}
                title={`Chưa có key nào trong namespace "${namespace}"`}
                description='Bắt đầu bằng cách nhập key mới ở trên, vd: "button.save", "form.title.pr".'
                hint="💡 Mỗi key có thể có nhiều locale. Ví dụ vi/en/ja sẽ là 3 cột riêng."
              />
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b bg-muted/30 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground min-w-[200px]">
                      Key
                    </th>
                    {locales.map((l) => (
                      <th
                        key={l}
                        className="px-3 py-2 text-left text-xs font-medium text-muted-foreground min-w-[200px]"
                      >
                        {l}
                      </th>
                    ))}
                    <th className="w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {keys.map((key) => {
                    const localeMap = byKey[key];
                    const firstEntry = localeMap
                      ? Object.values(localeMap)[0]
                      : undefined;
                    return (
                      <tr key={key} className="border-b last:border-b-0 hover:bg-muted/30">
                        <td className="px-3 py-2 align-top">
                          <code className="text-xs">{key}</code>
                        </td>
                        {locales.map((l) => {
                          const cell = localeMap?.[l];
                          const isEditing =
                            draft?.key === key && draft.locale === l;
                          return (
                            <td key={l} className="px-3 py-1 align-top">
                              {isEditing && draft ? (
                                <div className="flex items-center gap-1">
                                  <Input
                                    value={draft.value}
                                    onChange={(e) =>
                                      setDraft({ ...draft, value: e.target.value })
                                    }
                                    className="h-7 text-xs"
                                    autoFocus
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter") {
                                        void handleSaveCell(key, l, draft.value);
                                      } else if (e.key === "Escape") {
                                        setDraft(null);
                                      }
                                    }}
                                  />
                                  <Button
                                    size="sm"
                                    className="h-7"
                                    onClick={() =>
                                      void handleSaveCell(key, l, draft.value)
                                    }
                                    disabled={busy}
                                  >
                                    Lưu
                                  </Button>
                                </div>
                              ) : (
                                <button
                                  className="text-left w-full text-xs hover:bg-muted rounded px-1 py-1 min-h-[28px] block"
                                  onClick={() =>
                                    setDraft({
                                      key,
                                      locale: l,
                                      value: cell?.value ?? "",
                                    })
                                  }
                                >
                                  {cell ? (
                                    cell.value
                                  ) : (
                                    <span className="text-muted-foreground italic">
                                      (chưa dịch)
                                    </span>
                                  )}
                                </button>
                              )}
                            </td>
                          );
                        })}
                        <td className="px-2 py-2 align-top">
                          {firstEntry ? (
                            <Button
                              size="icon"
                              variant="ghost"
                              className="size-7"
                              onClick={() => void handleDelete(firstEntry.id)}
                              title="Xoá entry trong locale đầu tiên"
                            >
                              <Trash2 className="size-3.5" />
                            </Button>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}
      </main>

      <AddLanguageDialog
        open={addLanguageOpen}
        existingLocales={locales}
        onClose={() => setAddLanguageOpen(false)}
        onAdded={async () => {
          await refresh();
        }}
      />
    </div>
  );
}
